import React from "react"
import { useWebsiteProducts } from "@/hooks/useWebsiteProducts"
import { useWebsiteConfig } from "@/hooks/useWebsiteConfig"
import { useCart } from "@/hooks/useCart"
import { useNavigate } from "react-router-dom"
import { ShoppingCart } from "lucide-react"
import { toast } from "sonner"
import { FilterSidebar } from "../components/FilterSidebar"
import { formatToDevanagariStyle } from "@/utilities/helper"

const getProductImage = (product: any) => {
  // Use thumbnail if available, otherwise first image, otherwise placeholder
  if (product?.thumbnail?.url) {
    return product.thumbnail.url
  }
  if (product?.images && product.images.length > 0 && product.images[0]?.url) {
    return product.images[0].url
  }

  return ""
}

export const Home: React.FC = () => {
  const [selectedSizes, setSelectedSizes] = React.useState<number[]>([])
  const [selectedColors, setSelectedColors] = React.useState<number[]>([])
  const [selectedCategories, setSelectedCategories] = React.useState<number[]>(
    []
  )

  const { data: websiteConfig } = useWebsiteConfig()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWebsiteProducts({
      per_page: 32,
      size_id: selectedSizes,
      color_id: selectedColors,
      category_id:
        selectedCategories.length > 0 ? selectedCategories[0] : undefined,
    })

  const { addItem, isAdding } = useCart()
  const navigate = useNavigate()
  const token = localStorage.getItem("website_token")

  // Observer for infinite scroll
  const observerTarget = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 1.0 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [observerTarget, hasNextPage, fetchNextPage])

  // Flatten pages
  const products = data?.pages.flatMap((page: any) => page.data.data) || []

  const handleAddToCart = (product: any) => {
    if (!token) {
      toast.error("Please login to add items to cart")
      navigate("/website/login")
      return
    }

    addItem({
      product_id: product.id,
      quantity: 1,
    })
  }

  const handleShopNow = () => {
    const productsSection = document.getElementById("products-section")
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: "smooth" })
    }
  }
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={websiteConfig?.banner_image || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=80"}
            alt="Hero Banner"
            className="w-full h-full object-cover opacity-50"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            {websiteConfig?.title || "New Season Arrivals"}
          </h1>
          <p className="mt-6 text-xl max-w-3xl mx-auto">
            {websiteConfig?.description || "Check out the latest trends in fashion. High quality materials, sustainable sourcing, and modern designs."}
          </p>
          <div className="mt-10">
            <button
              onClick={handleShopNow}
              className="bg-indigo-600 px-8 py-3 rounded-md font-medium hover:bg-indigo-700 transition"
            >
              Shop Now
            </button>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      <div
        id="products-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
      >
        {/* <h2 className="text-2xl font-bold text-gray-900 mb-8">Trending Now</h2> */}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <FilterSidebar
            selectedSizes={selectedSizes}
            setSelectedSizes={setSelectedSizes}
            selectedColors={selectedColors}
            setSelectedColors={setSelectedColors}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
          {isLoading ? (
            <div className="text-center py-12 lg:col-span-3">
              <div className="text-gray-500">Loading products...</div>
            </div>
          ) : products.length === 0 ? (
            <div className="lg:col-span-3 text-center py-12">
              <div className="text-gray-500">No products available</div>
            </div>
          ) : (
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {products.map((product: any) => (
                  <div
                    key={product.id}
                    className="group relative cursor-pointer"
                    onClick={() => navigate(`/website/product/${product.id}`)}
                  >
                    <div className="h-[230px] aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200 xl:aspect-w-7 xl:aspect-h-8">
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        className="h-full w-full object-cover object-center group-hover:opacity-75 transition"
                      />
                    </div>
                    <h3 className="mt-4 text-sm text-gray-700 hover:text-indigo-600">
                      {product.name}
                    </h3>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-lg font-medium text-gray-900">
                        Rs.{" "}
                        {formatToDevanagariStyle(product.sell_price) || "0.00"}
                      </p>
                      {product.stock > 0 ? (
                        <span className="text-xs text-green-600">In Stock</span>
                      ) : (
                        <span className="text-xs text-red-600">
                          Out of Stock
                        </span>
                      )}
                    </div>
                    {product.tags && product.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {product.tags.slice(0, 2).map((tag: any) => (
                          <span
                            key={tag.id}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAddToCart(product)
                      }}
                      disabled={isAdding || product.stock === 0}
                      className="mt-2 w-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 rounded-sm text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <ShoppingCart size={16} />
                      {isAdding ? "Adding..." : "Add to Cart"}
                    </button>
                  </div>
                ))}
              </div>

              {/* Infinite Scroll Target */}
              <div
                ref={observerTarget}
                className="mt-12 text-center h-10 w-full flex justify-center items-center"
              >
                {isFetchingNextPage && (
                  <div className="text-gray-500">Loading more...</div>
                )}
                {!hasNextPage && products.length > 0 && (
                  <div className="text-gray-400 text-sm">No more products</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
